/* eslint-disable react-hooks/exhaustive-deps */
import { useApi } from '@backstage/core-plugin-api';
import { useContext, useEffect, useState, useCallback } from 'react';
import { promptApiRef } from '../../../apis/promptManagementApi';
import { useEnvScopes } from './useEnvScopes';
import { AuthContext } from '../../../providers/AuthProvider';
import type { AgentType } from '../types/chat';

/**
 * Hook to fetch prompts for Ellis DNT Chat based on environment and selected tool
 * Uses domain, subdomain, and persona IDs specific to the current agent/tool
 *
 * @param currentAgent - The currently selected tool/agent (jira-agile, ado-agile, knowledge-graph)
 * @returns Tuple of [prompts, loading, createPrompt]
 */
export const useEllisPrompts = (currentAgent: AgentType) => {
  const promptManagementApi = useApi(promptApiRef);
  const { domainId, subDomainId, personaId } = useEnvScopes(currentAgent);
  const { profEmail } = useContext(AuthContext);

  const [prompts, setPrompts] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Memoized function to fetch prompts
  const fetchPrompts = useCallback(async () => {
    if (!domainId || !subDomainId || !personaId) {
      setPrompts([]);
      return;
    }

    try {
      setLoading(true);
      const response = await promptManagementApi.getPromptsByDomain(
        domainId,
        subDomainId,
        personaId,
      );

      // Extract prompts array from response
      // Response structure: { prompts: [...], ... }
      const promptsArray = response?.prompts || response || [];
      setPrompts(Array.isArray(promptsArray) ? promptsArray : []);
    } catch (error) {
      console.error('Error fetching Ellis prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  }, [domainId, subDomainId, personaId, promptManagementApi]);

  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  const createPrompt = useCallback(
    async (promptData: any) => {
      if (!promptData || !domainId || !subDomainId || !personaId) return null;

      try {
        setCreateError(null);

        // Build the request payload matching the actual API structure from AddEditPrompt
        const promptPayload = {
          prompts: [
            {
              product: domainId,
              subproduct: subDomainId,
              persona: [personaId],
              promptkey: promptData.title || promptData.promptkey,
              promptdetail: promptData.promptText || promptData.promptdetail,
              systemPrompt:
                promptData.systemPromptText || promptData.systemprompt || '',
              authoremail: profEmail,
              authordisplayname:
                profEmail?.split('@')[0].split('.').join(' ') || '',
              tags: promptData.tags || [],
            },
          ],
          force: false,
        };

        const response = await promptManagementApi.createPrompt(promptPayload);

        if (response?.status?.[0]?.status === 'success') {
          console.log('Prompt created successfully');
          // Refresh the prompts list after successful creation
          await fetchPrompts();
          return { success: true, data: response };
        } else if (response?.status?.[0]?.status === 'failure') {
          const error = response.status[0].message || 'Failed to create prompt';
          console.error('Failed to create prompt:', error);
          setCreateError(error);
          return {
            success: false,
            error,
            similarPrompts: response.status[0].similar_prompts,
          };
        }
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'Failed to create prompt';
        console.error('Error creating prompt:', error);
        setCreateError(errorMsg);
        return { success: false, error: errorMsg };
      }
    },
    [
      promptManagementApi,
      domainId,
      subDomainId,
      personaId,
      profEmail,
      fetchPrompts,
    ],
  );

  return [prompts, loading, createPrompt, createError];
};
